// Combo discount academic criteria: University -> Course -> Regulation -> Semester.
//
// `options` is GET /api/combo-discount-prices/academic-options: every valid
// { universityId, universityName, courseId, courseName, regulationYearId,
//   regulationName, semesterId, semesterName } combination (from course_books).
// Each level only offers what is valid under the levels before it; nothing
// ticked at a level means "all of them". Regulations are picked by name (a
// regulation such as "R22" is a separate row for every course) and sent as the
// ids of that name within the courses in scope.

export const EMPTY_SELECTION = Object.freeze({
  universityIds: [],
  courseIds: [],
  regulationNames: [],
  semesterIds: [],
});

const unique = (values) => [...new Set(values)];
const within = (selected, value) => selected.length === 0 || selected.includes(value);

// The options rows in scope of the selections up to (not including) `level`.
function inScope(options, sel, level) {
  const order = ["universityIds", "courseIds", "regulationNames", "semesterIds"];
  const upTo = order.indexOf(level);
  return options.filter(
    (o) =>
      sel.universityIds.includes(o.universityId) &&
      (upTo < 2 || within(sel.courseIds, o.courseId)) &&
      (upTo < 3 || within(sel.regulationNames, o.regulationName)),
  );
}

export function universityOptions(options) {
  const byId = new Map();
  options.forEach((o) => byId.set(o.universityId, o.universityName));
  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

// Courses of the selected universities; the university is named when more than
// one is selected, since course names repeat across universities.
export function courseOptions(options, sel) {
  const byId = new Map();
  inScope(options, sel, "courseIds").forEach((o) =>
    byId.set(o.courseId, { id: o.courseId, name: o.courseName, universityName: o.universityName }),
  );
  const several = sel.universityIds.length > 1;
  return [...byId.values()]
    .map((c) => ({ id: c.id, name: several ? `${c.name} (${c.universityName})` : c.name }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export function regulationOptions(options, sel) {
  return unique(inScope(options, sel, "regulationNames").map((o) => o.regulationName))
    .sort((a, b) => String(a).localeCompare(String(b)))
    .map((name) => ({ id: name, name }));
}

export function semesterOptions(options, sel) {
  const byId = new Map();
  inScope(options, sel, "semesterIds").forEach((o) => byId.set(o.semesterId, o.semesterName));
  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.id - b.id);
}

// Drops every selection that is no longer valid under the levels before it
// (e.g. a course of a university that was unticked), level by level.
export function prune(options, sel) {
  const valid = (list, choices) => {
    const ids = new Set(choices.map((c) => c.id));
    return unique(list).filter((v) => ids.has(v));
  };
  const universityIds = valid(sel.universityIds, universityOptions(options));
  if (universityIds.length === 0) return EMPTY_SELECTION;
  let next = { ...EMPTY_SELECTION, universityIds };
  next = { ...next, courseIds: valid(sel.courseIds, courseOptions(options, next)) };
  next = { ...next, regulationNames: valid(sel.regulationNames, regulationOptions(options, next)) };
  next = { ...next, semesterIds: valid(sel.semesterIds, semesterOptions(options, next)) };
  return next;
}

// Ticks or unticks one value at a level, then clears what that makes invalid.
export function toggle(options, sel, level, value) {
  const list = sel[level];
  const changed = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  return prune(options, { ...sel, [level]: changed });
}

const sortedNumbers = (values) => unique(values).sort((a, b) => a - b);

// The request body fields: ids only, de-duplicated, and only ones still valid.
export function toPayload(options, selection) {
  const sel = prune(options, selection);
  const regulationYearIds =
    sel.regulationNames.length === 0
      ? []
      : inScope(options, sel, "regulationNames")
          .filter((o) => sel.regulationNames.includes(o.regulationName))
          .map((o) => o.regulationYearId);
  return {
    universityIds: sortedNumbers(sel.universityIds),
    courseIds: sortedNumbers(sel.courseIds),
    regulationYearIds: sortedNumbers(regulationYearIds),
    semesterIds: sortedNumbers(sel.semesterIds),
  };
}

// A saved combo discount's criteria (ids from the API) as a selection.
export function fromSaved(options, saved) {
  const names = new Map(options.map((o) => [o.regulationYearId, o.regulationName]));
  return prune(options, {
    universityIds: saved?.universityIds || [],
    courseIds: saved?.courseIds || [],
    regulationNames: unique((saved?.regulationYearIds || []).map((id) => names.get(id)).filter(Boolean)),
    semesterIds: saved?.semesterIds || [],
  });
}
